import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  AccordionDetails,
  Typography,
  Box,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordion from '@mui/material/Accordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Accordion = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const PromotionResultDialog = ({ open, onClose, promotionResults, equipment }) => {
  if (!open) return null;
  let titleText = '';
  let titleIcon = '';
  const totalPromotions = Object.keys(promotionResults.equipment).length;
  const failedPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'error'
  ).length;
  const partialPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'partial'
  ).length;
  const succeededPromotions = Object.keys(promotionResults.equipment).filter(
    (equipKey) => promotionResults.equipment[equipKey].status === 'ok'
  ).length;

  switch (promotionResults.status) {
    case 'success':
      titleText = 'Publish Successful';
      titleIcon = <CheckCircleIcon color="success" />;
      break;
    case 'partial':
      titleText = 'Publish Partial Success';
      titleIcon = <WarningIcon color="warning" />;
      break;
    case 'failure':
      titleText = 'Publish Failure';
      titleIcon = <ErrorIcon color="error" />;
      break;
    default:
      break;
  }

  function getEquipmentIcon(status) {
    switch (status) {
      case 'ok':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  }

  function getPointIcon(status) {
    switch (status) {
      case 'ok':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  }

  return (
    <Dialog open={open} maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          {titleIcon}
          <Typography sx={{ paddingLeft: 1 }} variant="h6">
            {titleText}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', minWidth: '600px' }}>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {failedPromotions} Failed Publish{failedPromotions !== 1 ? 'es' : ''}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {partialPromotions} Partial Publish{partialPromotions !== 1 ? 'es' : ''}
          </Typography>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {succeededPromotions} Succeeded Publish{succeededPromotions !== 1 ? 'es' : ''}
          </Typography>
        </Box>
        <Box style={{ overflowY: 'auto', maxHeight: '300px', height: '300px' }}>
          {Object.keys(promotionResults.equipment).map((equipId) => {
            const equipResult = promotionResults.equipment[equipId];
            return (
              <Accordion key={equipId} border="1">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    {getEquipmentIcon(equipResult.status)}
                    <Typography sx={{ paddingLeft: 1 }}>{equipment.get(equipId).name}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List sx={{ width: '100%' }}>
                    <ListItem alignItems="flex-start" key={equipId}>
                      <ListItemAvatar>{getEquipmentIcon(equipResult.status)}</ListItemAvatar>
                      <ListItemText
                        primary={equipResult.equip.name}
                        secondary={equipResult.message.map((v) => v.message)}
                      />
                      <Divider variant="inset" />
                    </ListItem>
                    {Object.keys(equipResult.points).map((topic) => {
                      const pointResult = equipResult.points[topic];
                      return (
                        <ListItem alignItems="flex-start" key={[topic, equipId].join('-')}>
                          <ListItemAvatar>{getPointIcon(pointResult.status)}</ListItemAvatar>
                          <ListItemText
                            primary={[
                              pointResult.point.device_id,
                              ...(pointResult.point.properties?.objectIdentifier ?? 'N/A'),
                              pointResult.point.properties?.objectName,
                            ].join(' ')}
                            secondary={pointResult?.message?.map((v) => v.message)}
                          />
                          <Divider variant="inset" />
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography component="div" sx={{ fontWeight: 'bold' }}>
            {totalPromotions} Total Publish{totalPromotions !== 1 ? 'es' : ''}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={onClose} color="success">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionResultDialog;
